import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { SEOHead } from '@components/SEOHead';

import { Button } from '@ui/Button';
import { Hero } from '@ui/Hero';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import { Info as InvestingInMutualFundsInfo } from '@guides/InvestingInMutualFunds';
import { Info as RetirementPlanningInfo } from '@guides/RetirementPlanning';
import { Info as SIPInvestmentStrategyInfo } from '@guides/SIPInvestmentStrategy';

export const GuidesPage = () => {
  const guides = [
    {
      id: InvestingInMutualFundsInfo.url,
      title: InvestingInMutualFundsInfo.displayTitle,
      description: InvestingInMutualFundsInfo.longDescription,
      readingTime: InvestingInMutualFundsInfo.readingTime,
      url: InvestingInMutualFundsInfo.url,
      available: true,
    },
    {
      id: SIPInvestmentStrategyInfo.url,
      title: SIPInvestmentStrategyInfo.displayTitle,
      description: SIPInvestmentStrategyInfo.longDescription,
      readingTime: SIPInvestmentStrategyInfo.readingTime,
      url: SIPInvestmentStrategyInfo.url,
      available: true,
    },
    {
      id: RetirementPlanningInfo.url,
      title: RetirementPlanningInfo.displayTitle,
      description: RetirementPlanningInfo.longDescription,
      readingTime: RetirementPlanningInfo.readingTime,
      url: RetirementPlanningInfo.url,
      available: true,
    },
    {
      id: 'goal-based-investing',
      title: 'Goal-based investment planning',
      description:
        'Learn how to invest for specific life goals like retirement, child education, home purchase, and more.',
      readingTime: '10-12 minutes',
      url: '#',
      available: false,
    },
  ];

  return (
    <>
      <SEOHead
        title={`Investment guides | ${CONTACT_INFO.company.name}`}
        description="Comprehensive investment guides covering mutual funds, SIP strategies, tax planning, and goal-based investing. Learn to invest smart with expert guidance."
        keywords="investment guides, mutual funds guide, SIP strategy, tax saving investments, financial planning, investment education"
        canonicalUrl={`${CONTACT_INFO.social.website}/guides`}
      />

      <div className="min-h-screen" style={{ backgroundColor: COLORS.pearl }}>
        <Header />

        <Hero
          title={
            <>
              Investment <span style={{ color: COLORS.gold }}>guides</span>
            </>
          }
          description="Comprehensive guides to help you make informed investment decisions and build wealth systematically"
          variant="gradient"
        />

        {/* Main Content */}
        <motion.section
          className="py-16"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="max-w-4xl mx-auto px-6">
            {/* Guides List */}
            <div className="space-y-6">
              {guides.map((guide, index) => (
                <motion.div
                  key={guide.id}
                  className="bg-white rounded-lg p-6 border border-gray-200"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900 flex-1 pr-3">
                          {guide.title}
                        </h3>
                      </div>

                      {guide.available && (
                        <div className="flex items-center text-sm text-gray-500 mb-3">
                          <Clock size={14} className="mr-1" />
                          {guide.readingTime}
                        </div>
                      )}

                      <p className="text-gray-600 leading-relaxed mb-4">
                        {guide.description}
                      </p>

                      {guide.available ? (
                        <Button
                          href={guide.url}
                          variant="outline"
                          size="sm"
                          icon={ArrowRight}
                          iconSize={14}
                        >
                          Read guide
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Clock}
                          iconSize={14}
                          disabled
                        >
                          Coming soon
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        <WhatsAppCTA
          title="Need personalized investment guidance?"
          description="Get personalized advice tailored to your specific goals and risk profile."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}, I've been reading your investment guides and would like personalized advice for my investment goals.`}
          buttonText="Get personalized advice"
          backgroundColor={COLORS.mist}
          buttonColor={COLORS.accent}
        />

        <Footer />
      </div>
    </>
  );
};
